import { createContext, useContext, useState, useEffect } from "react";
import { t, translateAction } from "../i18n";
import { authService } from "../services/authService";
import { useAuth } from "./AuthContext";
import { installZodErrorMap } from "../lib/zodSetup";

const LangCtx = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lumfi_lang") || "en");
  // Subscribe to AuthContext so we hydrate AS SOON AS the user is
  // authenticated (an effect on mount would be too early — /api/auth/me
  // is a 401 then because AuthProvider hasn't validated the token yet).
  const { user } = useAuth();

  useEffect(() => {
    localStorage.setItem("lumfi_lang", lang);
    // Refresh Zod's global error map so any subsequent `.safeParse()`
    // produces messages in the active language. Idempotent.
    installZodErrorMap(lang);
  }, [lang]);

  // Hydrate from the logged-in user. Runs whenever the user becomes
  // available (login OR page refresh that restores a session) so the
  // in-app language follows the user's saved profile preference across
  // devices, not just within one localStorage.
  useEffect(() => {
    const saved = user?.language;
    if (saved === "en" || saved === "es" || saved === "pt") {
      setLang((prev) => (prev === saved ? prev : saved));
    }
  }, [user?.language]);

  // Persist a lang change to the user record (best-effort — failures are
  // silent because localStorage already holds the new value). Only call
  // the server when we have an authenticated user; before login the PUT
  // would 401.
  const setLangWithSync = (next) => {
    setLang(next);
    if (user?.id) {
      authService.setLanguage(next).catch((err) =>
        console.warn("language sync failed", err)
      );
    }
  };

  const tr = (key, vars) => t(lang, key, vars);
  const translateActionFn = (code, params, fallback, formatMoney, currency) =>
    translateAction(lang, code, params, fallback, formatMoney, currency);
  return (
    <LangCtx.Provider value={{ lang, setLang: setLangWithSync, t: tr, translateAction: translateActionFn }}>
      {children}
    </LangCtx.Provider>
  );
}

export const useLang = () => useContext(LangCtx);
